import React, { useState } from "react";
import { Modal } from "@mui/material";
import { XIcon, InformationCircleIcon } from "@heroicons/react/solid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import Loader from "../../components/Loader";
import usePut from "../../hooks/usePut";

const EditSalaryStructureModal = ({
  isOpen,
  onClose,
  onSuccess,
  salaryStructureId,
  data,
}) => {
  const parseDateValue = (val) => {
    if (!val) return null;
    const timestamp = Number(val);
    if (!isNaN(timestamp) && timestamp > 0) {
      return new Date(timestamp * 1000);
    }
    return null;
  };

  const [formData, setFormData] = useState({
    name: data?.name || "",
    show_from_date: parseDateValue(data?.show_from_date),
    show_to_date: parseDateValue(data?.show_to_date),
    from_date: parseDateValue(data?.from_date),
    to_date: parseDateValue(data?.to_date),
    can_update: Boolean(data?.can_update),
  });
  const [errors, setErrors] = useState({});

  // usePut hook for update operation
  const { handleAuth } = usePut({
    url: "salary-structure/update",
    refreshUrl: "salary-structure/list",
    onSuccess: (res) => {
      toast.success(res?.data?.message || "Salary Structure Updated Successfully");
      onClose();
      onSuccess?.();
    },
    onError: (err) => {
      console.error("Update error:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.name ||
        "Failed to update salary structure";
      toast.error(errorMessage);
    },
  });

  const isEditable = Boolean(formData.can_update);

  const formatDateForApi = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handleChange = (e) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDateChange = (field, date) => {
    if (!isEditable) return;
    setFormData((prev) => ({
      ...prev,
      [field]: date,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isEditable) {
      toast.error("This salary structure is read-only and cannot be updated");
      return;
    }

    if (!formData.name.trim()) {
      setErrors({ name: "The name field is required." });
      toast.error("Please enter the structure name");
      return;
    }

    const payload = {
      salary_structure_id: salaryStructureId || data?.salary_structure_id,
      name: formData.name.trim(),
      show_from_date: formatDateForApi(formData.show_from_date),
      show_to_date: formatDateForApi(formData.show_to_date),
      from_date: formatDateForApi(formData.from_date),
      to_date: formatDateForApi(formData.to_date),
    };

    handleAuth.mutate(payload);
  };

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="edit-salary-structure-modal">
      <div className="w-[90%] md:w-[60%] lg:w-[45%] bg-white absolute left-1/2 -translate-x-1/2 top-[10%] max-h-[85vh] overflow-y-auto rounded-lg shadow-xl focus:outline-none">
        <div className="px-6 py-4 divide-y divide-gray-200">
          <div className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-gray-800 text-2xl font-semibold roboto">
                  {isEditable ? "Edit Salary Structure" : "Salary Structure Details"}
                </h3>
              </div>
              <div className="w-6 h-6 cursor-pointer text-gray-500 hover:text-black" onClick={onClose}>
                <XIcon className="w-full h-full" />
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="py-4 space-y-5">
            {!isEditable && (
              <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium rounded flex items-center gap-2">
                <InformationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <span>This salary structure cannot be updated.</span>
              </div>
            )}

            {/* Structure Name */}
            <div>
              <label className="label">
                Structure Name{isEditable && <span className="mandatory">*</span>}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={!isEditable || handleAuth.isLoading}
                placeholder="e.g. Standard Executive Structure"
                className={`input ${errors.name ? "border-red-500" : "border-gray-300"} ${!isEditable ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""
                  }`}
                required={isEditable}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Display Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Display From Date</label>
                <DatePicker
                  selected={formData.show_from_date}
                  onChange={(date) => handleDateChange("show_from_date", date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  portalId="root"
                  popperClassName="!z-[1400]"
                  disabled={!isEditable || handleAuth.isLoading}
                  className={`input border-gray-300 ${!isEditable ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""
                    }`}
                  wrapperClassName="w-full"
                />
              </div>
              <div>
                <label className="label">Display To Date</label>
                <DatePicker
                  selected={formData.show_to_date}
                  onChange={(date) => handleDateChange("show_to_date", date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  portalId="root"
                  popperClassName="!z-[1400]"
                  disabled={!isEditable || handleAuth.isLoading}
                  className={`input border-gray-300 ${!isEditable ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""
                    }`}
                  wrapperClassName="w-full"
                  minDate={formData.show_from_date}
                />
              </div>
            </div>

            {/* Effective Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Effective From Date</label>
                <DatePicker
                  selected={formData.from_date}
                  onChange={(date) => handleDateChange("from_date", date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  portalId="root"
                  popperClassName="!z-[1400]"
                  disabled={!isEditable || handleAuth.isLoading}
                  className={`input border-gray-300 ${!isEditable ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""
                    }`}
                  wrapperClassName="w-full"
                />
              </div>
              <div>
                <label className="label">Effective To Date</label>
                <DatePicker
                  selected={formData.to_date}
                  onChange={(date) => handleDateChange("to_date", date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  portalId="root"
                  popperClassName="!z-[1400]"
                  disabled={!isEditable || handleAuth.isLoading}
                  className={`input border-gray-300 ${!isEditable ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""
                    }`}
                  wrapperClassName="w-full"
                  minDate={formData.from_date}
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={handleAuth.isLoading}
                className="py-2 px-5 rounded font-semibold border border-gray-300 hover:bg-gray-100 text-gray-700 transition"
              >
                {isEditable ? "Cancel" : "Close"}
              </button>
              {isEditable && (
                <button
                  type="submit"
                  disabled={handleAuth.isLoading}
                  className="bg-[color:var(--color1)] hover:opacity-90 text-white py-2 px-6 rounded font-semibold flex items-center justify-center space-x-2 transition disabled:opacity-50"
                >
                  {handleAuth.isLoading ? (
                    <>
                      <Loader className="h-4 w-4 text-white" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <span>Update Structure</span>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default EditSalaryStructureModal;
